// @flow
import React, { Component, useRef, useCallback } from 'react';
import moment from 'moment';
import bem from '@justpark/helpers/src/bem/bem';
import useOutsideClickHandler from '../../../hooks/useOutsideClickHandler';
import TimeWheelSelect from './TimeWheelSelect';
import TimeSelect from './TimeSelect';
import Brand from '../../UI/Brand';
import LazySingleDatePicker from '../../Common/LazySingleDatePicker';

import '../../../../static/scss/components/datePicker.scss';

type Tab = {
  id: number,
  label: string,
  active: boolean,
  containerClass: string,
  focused: boolean
};

type InitialState = {
  enabled: boolean,
  isMobile: boolean,
  isDesktop: boolean,
  isPortrait: boolean,
  tabs: Array<Tab>
};

const initialState = {
  enabled: true,
  isMobile: false,
  isDesktop: false,
  isPortrait: false,
  tabs: [
    {
      id: 0,
      label: 'Arriving',
      active: true,
      containerClass: 'from',
      focused: false
    },
    {
      id: 0,
      label: 'Leaving',
      active: false,
      containerClass: 'to',
      focused: false
    }
  ]
};

const activateTab = (state, index) => {
  const tabs = state.tabs.map(elem => ({ ...elem, active: false }));
  tabs[index].active = true;
  return tabs;
};

const formatDate = date => {
  if (!date) {
    return moment().format('[Today at] HH:mm');
  }

  return date.isSame(moment(), 'day')
    ? date.format('[Today at] HH:mm')
    : date.format('Do MMM [at] HH:mm');
};

const device = __CLIENT__ ? window.matchMedia('(max-width: 860px)') : null;
const orientation = __CLIENT__
  ? window.matchMedia('(orientation: portrait)')
  : null;

function handleMediaChange(elem, handler) {
  return event => {
    elem.setState(handler(event));
  };
}

type BothProps = {
  onClose: () => void,
  onDateChange: () => void,
  isOpen: boolean,
  hideLeaving: boolean
};

type Props = BothProps & {
  activeTab: string,
  arriving?: string,
  leaving?: string,
  showTimePicker?: boolean,
  checkTime?: () => void
};

type DatePickerWithOutsideClickProps = BothProps & {
  tabs: Array<any>,
  updateFocusOfCurrentTab: () => void,
  isDesktop: boolean,
  isMobile: boolean,
  isPortrait: boolean,
  setDate: () => void,
  switchElems: () => void,
  hideLeaving: boolean,
  showTimePicker: boolean,
  checkTime: () => void
};

const DatePickerWithOutsideClick = (props: DatePickerWithOutsideClickProps) => {
  const node = useRef();

  const {
    onClose,
    tabs,
    showTimePicker,
    onDateChange,
    updateFocusOfCurrentTab,
    checkTime,
    isDesktop,
    isMobile,
    isPortrait,
    isOpen,
    setDate,
    switchElems,
    hideLeaving
  } = props;

  const outsideClickOnClose = useCallback(
    () => {
      if (!isMobile) {
        checkTime();
        onClose();
      }
    },
    [onClose, checkTime, isMobile]
  );
  useOutsideClickHandler(node, outsideClickOnClose, isOpen);

  return (
    <Brand>
      <div ref={node} className="c-datepicker">
        {!hideLeaving && (
          <ul className="c-datepicker__tabs">
            {/* eslint-disable jsx-a11y/no-static-element-interactions */}
            {tabs.map((item, index) => {
              const className = `c-datepicker__tab${
                item.active ? ' c-datepicker__tab--active' : ''
              }`;
              return (
                <li
                  key={index}
                  className={className}
                  onClick={() => switchElems(index)}
                >
                  {item.label}
                </li>
              );
            })}
          </ul>
        )}

        {tabs.map((item, key) => {
          if (!props[item.label.toLowerCase()]) {
            return null;
          }

          const { containerClass, active } = item;
          const className = bem('c-datepicker__container', null, {
            containerClass,
            active,
            'date-only': !showTimePicker
          });

          return (
            <div key={key} className={className}>
              <span className="icon icon-calendar" />
              <LazySingleDatePicker
                key={key}
                focused={
                  (isDesktop && item.active) || (isMobile && item.focused)
                }
                daySize={isMobile ? 44 : 30}
                withFullScreenPortal={isMobile}
                numberOfMonths={1}
                orientation={isPortrait && isMobile ? 'vertical' : 'horizontal'}
                date={props[item.label.toLowerCase()].clone()}
                onDateChange={date => {
                  setDate(item.label.toLowerCase(), date);
                }}
                onFocusChange={params => {
                  if (item.active) {
                    updateFocusOfCurrentTab(key, params.focused);
                  }
                }}
                displayFormat="D/MM/YYYY"
                hideKeyboardShortcutsPanel
                enableOutsideDays
              />
              {item.active && showTimePicker ? (
                <TimeWheelSelect
                  callback={onDateChange}
                  type={item.label}
                  value={props[item.label.toLowerCase()].format('H:mm')}
                />
              ) : null}
              {showTimePicker && (
                <TimeSelect
                  callback={onDateChange}
                  type={item.label}
                  value={props[item.label.toLowerCase()].format('H:mm')}
                />
              )}
              <strong>{item.label} at:</strong>{' '}
              <span>{formatDate(props[item.label.toLowerCase()])}</span>
              <Brand secondary backgroundColor>
                <a
                  onClick={() => {
                    checkTime();
                    onClose();
                  }}
                  className="c-datepicker__close-bt"
                  data-cy="datepicker-close-button"
                >
                  Done
                </a>
              </Brand>
            </div>
          );
        })}
      </div>
    </Brand>
  );
};

type State = InitialState & {
  tabs: number
};

class DatePicker extends Component<Props, State> {
  constructor(props) {
    super(props);

    let state = { ...initialState };
    state = {
      ...initialState,
      tabs: activateTab(state, props.activeTab === 'from' ? 0 : 1)
    };

    this.state = state;

    this.setDate = this.setDate.bind(this);

    this.onMatch = handleMediaChange(this, event => ({
      isMobile: event.matches,
      isDesktop: !event.matches
    }));
    this.onOrientationMatch = handleMediaChange(this, event => ({
      isPortrait: event.matches
    }));
  }

  componentWillMount() {
    if (__CLIENT__) {
      this.device = device;
      this.orientation = orientation;

      // Handle initial loading state
      this.state.isMobile = device.matches;
      this.state.isDesktop = !device.matches;
      this.state.isPortrait = orientation.matches;
    }
  }

  componentDidMount() {
    device.addListener(this.onMatch);
    orientation.addListener(this.onOrientationMatch);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.activeTab !== this.props.activeTab) {
      const { state } = this;
      this.setState({
        ...state,
        tabs: activateTab(state, nextProps.activeTab === 'from' ? 0 : 1)
      });
    }
  }

  componentWillUnmount() {
    device.removeListener(this.onMatch);
    orientation.removeListener(this.onOrientationMatch);
  }

  setDate(which, date) {
    this.props.onDateChange(which, 'date', date);
  }

  switchElems(index) {
    if (this.state.tabs[index].active === true) {
      return;
    }
    const { state } = this;
    const tabs = activateTab(state, index);
    this.setState({ tabs });
  }

  render() {
    const {
      onClose,
      checkTime,
      showTimePicker,
      hideLeaving,
      onDateChange,
      arriving,
      leaving,
      isOpen
    } = this.props;
    const { isDesktop, tabs, isMobile, isPortrait } = this.state;

    const updateFocusOfCurrentTab = (key, focused) => {
      tabs[key].focused = focused;
      this.setState({ tabs });
    };

    return (
      <DatePickerWithOutsideClick
        onClose={onClose}
        checkTime={checkTime}
        updateFocusOfCurrentTab={updateFocusOfCurrentTab}
        onDateChange={onDateChange}
        showTimePicker={showTimePicker}
        hideLeaving={hideLeaving}
        isDesktop={isDesktop}
        isMobile={isMobile}
        isPotrait={isPortrait}
        tabs={tabs}
        setDate={this.setDate}
        arriving={arriving}
        leaving={leaving}
        isOpen={isOpen}
        switchElems={this.switchElems.bind(this)}
      />
    );
  }
}

DatePicker.defaultProps = {
  arriving: moment(),
  leaving: moment(),
  checkTime: () => {},
  showTimePicker: true
};

export default DatePicker;
